import { useForm } from "@inertiajs/react"
import type { FormEventHandler } from "react"

interface PtoRequestData {
  id?: number
  user_id?: number
  request_type: number
  start_date: string | null
  end_date: string | null
  start_time: string
  start_period: string
  end_time: string
  end_period: string
  hours_requested: number
  full_day: boolean
  reason: string
}

interface RequestType {
  value: number
  label: string
}

interface Employee {
  id: number
  name: string
}

interface Props {
  ptoRequest: PtoRequestData
  requestTypes: RequestType[]
  employees?: Employee[]
  canManage?: boolean
  errors?: Record<string, string[]>
  onCancel: () => void
}

const TIME_OPTIONS = [
  "12:00",
  "1:00",
  "2:00",
  "3:00",
  "4:00",
  "5:00",
  "6:00",
  "7:00",
  "8:00",
  "9:00",
  "10:00",
  "11:00",
]

const PERIOD_OPTIONS = ["AM", "PM"]

// Legacy request type constants
const TYPE_SICK = 1

export default function PtoRequestForm({
  ptoRequest,
  requestTypes,
  employees,
  canManage,
  errors,
  onCancel,
}: Props) {
  const { data, setData, post, put, processing, transform } =
    useForm<PtoRequestData>({
      user_id: ptoRequest.user_id,
      request_type: ptoRequest.request_type,
      start_date: ptoRequest.start_date,
      end_date: ptoRequest.end_date,
      start_time: ptoRequest.start_time || "12:00",
      start_period: ptoRequest.start_period || "AM",
      end_time: ptoRequest.end_time || "12:00",
      end_period: ptoRequest.end_period || "AM",
      hours_requested: ptoRequest.hours_requested,
      full_day: ptoRequest.full_day,
      reason: ptoRequest.reason,
    })

  transform((data) => ({ pto_request: data }))

  const isSto = data.request_type === TYPE_SICK

  const submit: FormEventHandler = (e) => {
    e.preventDefault()
    if (ptoRequest.id) {
      put(`/pto_requests/${ptoRequest.id}`)
    } else {
      post("/pto_requests")
    }
  }

  const handleFullDayChange = (checked: boolean) => {
    setData((prev) => ({
      ...prev,
      full_day: checked,
      hours_requested: checked ? 8 : prev.hours_requested,
    }))
  }

  const handleTypeChange = (newType: number) => {
    setData((prev) => ({
      ...prev,
      request_type: newType,
      // Default STO to full day like legacy
      full_day: newType === TYPE_SICK ? true : prev.full_day,
    }))
  }

  const inputClass =
    "mt-1 block w-full rounded-md border-hpi-light-gray shadow-sm focus:border-hpi-active-blue focus:ring-hpi-active-blue"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"
  const errorClass = "mt-1 text-sm text-hpi-red"

  return (
    <form onSubmit={submit} className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        {canManage && employees && isSto && (
          <div className="md:col-span-2">
            <label className={labelClass}>Employee</label>
            <select
              className={inputClass}
              value={data.user_id ?? ""}
              onChange={(e) =>
                setData(
                  "user_id",
                  e.target.value ? parseInt(e.target.value) : undefined,
                )
              }
              required
            >
              <option value="">Select an employee...</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
            {errors?.user_id && (
              <p className={errorClass}>{errors.user_id[0]}</p>
            )}
          </div>
        )}

        <div>
          <label className={labelClass}>Request Type</label>
          <select
            className={inputClass}
            value={data.request_type}
            onChange={(e) => handleTypeChange(parseInt(e.target.value))}
          >
            {requestTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          {errors?.request_type && (
            <p className={errorClass}>{errors.request_type[0]}</p>
          )}
        </div>

        {isSto && (
          <div className="flex items-center pt-6">
            <input
              type="checkbox"
              id="full_day"
              className="border-hpi-light-gray text-hpi-active-blue focus:ring-hpi-active-blue h-4 w-4 rounded"
              checked={data.full_day}
              onChange={(e) => handleFullDayChange(e.target.checked)}
            />
            <label
              htmlFor="full_day"
              className="text-hpi-dark-gray ml-2 text-sm"
            >
              Full Day Off
            </label>
          </div>
        )}

        {!isSto && (
          <div className="flex items-center pt-6">
            <input
              type="checkbox"
              id="full_day"
              className="border-hpi-light-gray text-hpi-active-blue focus:ring-hpi-active-blue h-4 w-4 rounded"
              checked={data.full_day}
              onChange={(e) => handleFullDayChange(e.target.checked)}
            />
            <label
              htmlFor="full_day"
              className="text-hpi-dark-gray ml-2 text-sm"
            >
              Full Day(s)
            </label>
          </div>
        )}

        {isSto && !data.full_day ? (
          <>
            <div>
              <label className={labelClass}>Beginning</label>
              <div className="mt-1 flex gap-2">
                <input
                  type="date"
                  className={`${inputClass} mt-0 w-[45%]`}
                  value={data.start_date ?? ""}
                  onChange={(e) => setData("start_date", e.target.value)}
                  required
                />
                <select
                  className={`${inputClass} mt-0 w-[30%]`}
                  value={data.start_time}
                  onChange={(e) => setData("start_time", e.target.value)}
                >
                  {TIME_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <select
                  className={`${inputClass} mt-0 w-[15%]`}
                  value={data.start_period}
                  onChange={(e) => setData("start_period", e.target.value)}
                >
                  {PERIOD_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              {errors?.start_date && (
                <p className={errorClass}>{errors.start_date[0]}</p>
              )}
            </div>

            <div>
              <label className={labelClass}>Ending</label>
              <div className="mt-1 flex gap-2">
                <input
                  type="date"
                  className={`${inputClass} mt-0 w-[45%]`}
                  value={data.end_date ?? ""}
                  onChange={(e) => setData("end_date", e.target.value)}
                />
                <select
                  className={`${inputClass} mt-0 w-[30%]`}
                  value={data.end_time}
                  onChange={(e) => setData("end_time", e.target.value)}
                >
                  {TIME_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <select
                  className={`${inputClass} mt-0 w-[15%]`}
                  value={data.end_period}
                  onChange={(e) => setData("end_period", e.target.value)}
                >
                  {PERIOD_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
              {errors?.end_date && (
                <p className={errorClass}>{errors.end_date[0]}</p>
              )}
            </div>
          </>
        ) : (
          <>
            <div>
              <label className={labelClass}>Start Date</label>
              <input
                type="date"
                className={inputClass}
                value={data.start_date ?? ""}
                onChange={(e) => setData("start_date", e.target.value)}
              />
              {errors?.start_date && (
                <p className={errorClass}>{errors.start_date[0]}</p>
              )}
            </div>

            <div>
              <label className={labelClass}>End Date</label>
              <input
                type="date"
                className={inputClass}
                value={data.end_date ?? ""}
                onChange={(e) => setData("end_date", e.target.value)}
              />
              {errors?.end_date && (
                <p className={errorClass}>{errors.end_date[0]}</p>
              )}
            </div>
          </>
        )}

        <div>
          <label className={labelClass}>Hours Requested</label>
          <input
            type="number"
            step="0.25"
            min="0"
            className={inputClass}
            value={data.hours_requested}
            onChange={(e) =>
              setData("hours_requested", parseFloat(e.target.value) || 0)
            }
            disabled={data.full_day}
          />
          {data.full_day && (
            <p className="text-hpi-med-gray mt-1 text-sm">
              Hours will be calculated based on number of full days
            </p>
          )}
          {errors?.hours_requested && (
            <p className={errorClass}>{errors.hours_requested[0]}</p>
          )}
        </div>
      </div>

      <div>
        <label className={labelClass}>Reason</label>
        <textarea
          className={inputClass}
          rows={4}
          value={data.reason}
          onChange={(e) => setData("reason", e.target.value)}
          placeholder={
            isSto
              ? "Please provide a reason for your sick time request..."
              : "Please provide a reason for your PTO request..."
          }
          required
        />
        {errors?.reason && <p className={errorClass}>{errors.reason[0]}</p>}
      </div>

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded-md border bg-white px-4 py-2 text-sm font-medium"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {processing
            ? "Submitting..."
            : ptoRequest.id
              ? "Update Request"
              : "Submit Request"}
        </button>
      </div>
    </form>
  )
}
